/**
 * Cookie utility functions for authentication
 */

/**
 * Sets a cookie with the given name, value, and expiration hours
 * @param {string} name - Cookie name
 * @param {string} value - Cookie value
 * @param {number} hours - Expiration time in hours (default: 1 hour)
 * @param {object} options - Additional cookie options
 */
export const setCookie = (name, value, hours = 1, options = {}) => {
  const expires = new Date();
  expires.setTime(expires.getTime() + hours * 60 * 60 * 1000);
  
  // Build cookie string
  let cookieString = `${name}=${encodeURIComponent(value)}; expires=${expires.toUTCString()}; path=/`;
  
  // Add Secure flag in production (HTTPS only)
  if (process.env.NODE_ENV === 'production') {
    cookieString += '; Secure';
  }
  
  // Add SameSite attribute
  cookieString += '; SameSite=Strict';
  
  // Apply any additional options
  if (options.domain) {
    cookieString += `; domain=${options.domain}`;
  }
  
  document.cookie = cookieString;
};

/**
 * Gets a cookie value by name
 * @param {string} name - Cookie name
 * @returns {string|null} - Cookie value or null if not found
 */
export const getCookie = (name) => {
  const nameEQ = name + "=";
  const cookies = document.cookie.split(';');
  
  for (let i = 0; i < cookies.length; i++) {
    let cookie = cookies[i];
    while (cookie.charAt(0) === ' ') {
      cookie = cookie.substring(1, cookie.length);
    }
    if (cookie.indexOf(nameEQ) === 0) {
      return decodeURIComponent(cookie.substring(nameEQ.length, cookie.length));
    }
  }
  return null;
};

/**
 * Deletes a cookie by name
 * @param {string} name - Cookie name
 * @param {object} options - Additional cookie options
 */
export const deleteCookie = (name, options = {}) => {
  // Set expiration date to the past
  let cookieString = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;
  
  // Add domain if specified
  if (options.domain) {
    cookieString += `; domain=${options.domain}`;
  }
  
  document.cookie = cookieString;
};

/**
 * Checks if a cookie exists
 * @param {string} name - Cookie name
 * @returns {boolean} - True if cookie exists
 */
export const hasCookie = (name) => {
  return getCookie(name) !== null;
};
